import type { SyncFrequency } from '../pages/DataSourcesPage/components/SyncFrequencyEditor';

/**
 * Converts a SyncFrequency object to AWS EventBridge cron expression
 * AWS cron format: cron(minute hour day-of-month month day-of-week year)
 *
 * Examples:
 * - Hourly at minute 15: cron(15 * * * ? *)
 * - Daily at 14:30: cron(30 14 * * ? *)
 * - Weekly on Monday at 14:30: cron(30 14 ? * 2 *) (1=Sunday in AWS)
 * - Monthly on day 15 at 14:30: cron(30 14 15 * ? *)
 * - Monthly on last day at 14:30: cron(30 14 L * ? *)
 */
export const syncFrequencyToCron = (freq: SyncFrequency): string => {
  const { type, minute, hour, dayOfWeek, dayOfMonth } = freq;

  switch (type) {
    case 'hourly':
      // Run at specified minute every hour
      return `cron(${minute} * * * ? *)`;

    case 'daily':
      // Run at specified time every day
      return `cron(${minute} ${hour || 0} * * ? *)`;

    case 'weekly': {
      // AWS day-of-week: 1=Sunday, 2=Monday, ..., 7=Saturday
      // Our dayOfWeek: 0=Sunday, 1=Monday, ..., 6=Saturday
      const awsDayOfWeek = (dayOfWeek || 0) + 1;
      return `cron(${minute} ${hour || 0} ? * ${awsDayOfWeek} *)`;
    }

    case 'monthly': {
      // Run on specified day of month (or 'L' for last day)
      const day = dayOfMonth === 'last' ? 'L' : dayOfMonth || 1;
      return `cron(${minute} ${hour || 0} ${day} * ? *)`;
    }

    default:
      return `cron(0 0 * * ? *)`;
  }
};

/**
 * Parses an AWS EventBridge cron expression to a SyncFrequency object
 * AWS cron format: cron(minute hour day-of-month month day-of-week year)
 */
export const cronToSyncFrequency = (cronExpr: string): SyncFrequency => {
  // Default fallback
  const defaultFreq: SyncFrequency = {
    type: 'daily',
    minute: 0,
    hour: 0,
  };

  try {
    // Extract the cron parts from "cron(...)"
    const match = cronExpr.match(/cron\((.+)\)/);
    if (!match) return defaultFreq;

    const parts = match[1].split(/\s+/);
    if (parts.length !== 6) return defaultFreq;

    const [minute, hour, dayOfMonth, , dayOfWeek] = parts;

    const minuteNum = parseInt(minute);
    const hourNum = parseInt(hour);

    // Hourly: hour is '*'
    if (hour === '*') {
      return {
        type: 'hourly',
        minute: minuteNum,
      };
    }

    // Weekly: dayOfWeek is not '?'
    if (dayOfWeek !== '?') {
      // Convert AWS day-of-week (1=Sunday) to our format (0=Sunday)
      const dayOfWeekNum = parseInt(dayOfWeek) - 1;
      return {
        type: 'weekly',
        minute: minuteNum,
        hour: hourNum,
        dayOfWeek: dayOfWeekNum,
      };
    }

    // Monthly: dayOfMonth is not '*'
    if (dayOfMonth !== '*') {
      const day = dayOfMonth === 'L' ? 'last' : parseInt(dayOfMonth);
      return {
        type: 'monthly',
        minute: minuteNum,
        hour: hourNum,
        dayOfMonth: day,
      };
    }

    // Daily: default case
    return {
      type: 'daily',
      minute: minuteNum,
      hour: hourNum,
    };
  } catch (error) {
    console.error('Failed to parse cron expression:', cronExpr, error);
    return defaultFreq;
  }
};

/**
 * Formats a SyncFrequency object to a human-readable string
 */
export const formatSyncFrequency = (freq: SyncFrequency): string => {
  const { type, minute, hour, dayOfWeek, dayOfMonth } = freq;
  const formatTime = (h: number, m: number) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  switch (type) {
    case 'hourly':
      return `Hourly at ${minute} minutes past`;
    case 'daily':
      return `Daily at ${formatTime(hour || 0, minute)} (UTC)`;
    case 'weekly': {
      const dayName = DAYS[dayOfWeek || 0];
      return `Weekly on ${dayName} at ${formatTime(hour || 0, minute)} (UTC)`;
    }
    case 'monthly': {
      const dayDesc = dayOfMonth === 'last' ? 'last day' : `day ${dayOfMonth}`;
      return `Monthly on ${dayDesc} at ${formatTime(hour || 0, minute)} (UTC)`;
    }
    default:
      return 'Not configured';
  }
};
